import { Box } from '@mentimeter/ragnar-ui/box';
import { useRef } from 'react';
import type { InteractiveCanvasComponents } from '@mentimeter/question-modules-types';
import { ScreenReaderOnly } from '@mentimeter/ragnar-ui/screenreader-only';
import { TitleSection } from '../../shared/components/title-section/TitleSection';
import { NonInteractiveVisualizationWrapper } from '../../shared/components/NonInteractiveVisualizationWrapper';
import { GuessTheNumberVisualization } from './GuessTheNumberVisualization';
import { ScreenReaderResults } from './ScreenReaderResults';

interface NumericalQuestionPresentationProps {
  title: string;
  styledTitle: Record<string, unknown>;
  label: string;
  longerDescription: string;
  fontSizeOffset: -2 | -1 | 0 | 1 | 2;
  results: { [id: number]: number } | undefined;
  shouldShowCorrect: boolean;
  CanvasVisualizationArea: InteractiveCanvasComponents['CanvasVisualizationArea'];
  shouldAnimate: boolean;
  shouldHideResults: boolean;
  responseRange: {
    min: number;
    max: number;
  };
  responseRangeScale: number;
  correctAnswerRange: {
    min: number;
    max: number;
  };
}

export const NumericalQuestionPresentation = ({
  title,
  styledTitle,
  label,
  longerDescription,
  fontSizeOffset,
  results: rawResults,
  shouldShowCorrect,
  CanvasVisualizationArea,
  shouldAnimate,
  shouldHideResults,
  responseRange,
  responseRangeScale,
  correctAnswerRange,
}: NumericalQuestionPresentationProps) => {
  const contentWidthRef = useRef<HTMLDivElement | null>(null);

  const ConditionalWrapper =
    CanvasVisualizationArea ?? NonInteractiveVisualizationWrapper;

  const results = shouldHideResults ? [] : rawResults;

  return (
    <Box width="100%" height="100%" data-testid="numerical-question-container">
      <TitleSection
        title={title}
        styledTitle={styledTitle}
        description={longerDescription}
        label={label}
        fontSizeOffset={fontSizeOffset}
        showTextSizeDropdown={true}
      />
      <ConditionalWrapper
        width="100%"
        height="100%"
        flex="1 1 auto"
        alignItems="center"
        justifyContent="center"
        ref={contentWidthRef}
      >
        <GuessTheNumberVisualization
          results={results || []}
          responseRangeMin={responseRange.min}
          responseRangeMax={responseRange.max}
          responseRangeScale={responseRangeScale}
          correctAnswerRange={correctAnswerRange}
          shouldShowCorrect={shouldShowCorrect}
          shouldAnimate={shouldAnimate}
        />

        <ScreenReaderOnly>
          <ScreenReaderResults
            results={results ?? []}
            rangeMin={responseRange.min}
            rangeMax={responseRange.max}
            responseRangeScale={responseRangeScale}
            shouldHideResults={shouldHideResults}
          />
        </ScreenReaderOnly>
      </ConditionalWrapper>
    </Box>
  );
};
